import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Logo, LanguageSwitcher } from "./Common";
import Notifications from "./Notifications";
import api, { apiErr } from "../lib/api";
import { Power, LogOut, Loader2 } from "lucide-react";

const MODES = [
  { key: "DEMO", color: "#3B82F6", labelKey: "modeDemo" },
  { key: "SEMI", color: "#F59E0B", labelKey: "modeSemi" },
  { key: "AUTO", color: "#8B5CF6", labelKey: "modeAuto" },
];

export default function Header({ account, onUpdate }) {
  const { t } = useLang();
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const killed = account?.kill_switch;

  const setMode = async (mode) => {
    try { const { data } = await api.put("/account/mode", { mode }); onUpdate(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleKill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/account/kill-switch?close_positions=${!killed}`);
      toast[data.kill_switch ? "error" : "success"](
        data.kill_switch ? `${t("killActive")} · ${data.closed_positions} position(s) fermée(s)` : t("reactivate"));
      onUpdate();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <header className="sticky top-0 z-40 bg-[#05050A] border-b border-[#1E2028]">
      <div className="flex items-center justify-between px-4 lg:px-6 h-16 gap-3">
        <Logo small />

        <div className="hidden md:flex items-center border border-[#1E2028] bg-[#0F1015]" data-testid="mode-toggle">
          {MODES.map((m) => (
            <button key={m.key} data-testid={`mode-${m.key}`} onClick={() => setMode(m.key)}
              className="px-3 lg:px-4 py-1.5 text-xs font-semibold transition-colors duration-200"
              style={account?.mode === m.key
                ? { background: m.color, color: "#05050A" }
                : { color: "#9CA3AF", background: "transparent" }}>
              {t(m.labelKey)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          <LanguageSwitcher />
          <Notifications />
          <button data-testid="kill-switch" onClick={toggleKill} disabled={busy}
            className={`flex items-center gap-2 px-3 lg:px-4 py-2 font-bold uppercase text-xs tracking-wider transition-colors duration-200 border ${
              killed
                ? "bg-[#0F1015] text-[#EF4444] border-[#EF4444] hover:bg-[#EF4444]/10"
                : "bg-[#DC2626] text-white border-[#DC2626] hover:bg-[#b91c1c] hover:shadow-[0_0_20px_rgba(220,38,38,0.5)]"
            }`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            <span className="hidden sm:inline">{killed ? t("reactivate") : t("killSwitch")}</span>
          </button>
          <button data-testid="logout-btn" onClick={logout}
            className="p-2 text-[#6B7280] hover:text-white transition-colors duration-200 border border-[#1E2028] hover:border-[#4B5563]">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* mobile mode toggle */}
      <div className="md:hidden flex items-center border-t border-[#1E2028]">
        {MODES.map((m) => (
          <button key={m.key} onClick={() => setMode(m.key)}
            className="flex-1 py-2 text-xs font-semibold transition-colors duration-200"
            style={account?.mode === m.key ? { background: m.color, color: "#05050A" } : { color: "#9CA3AF" }}>
            {t(m.labelKey)}
          </button>
        ))}
      </div>
    </header>
  );
}
